import { VerificationStatusType } from 'types/compliance';
import { PartyToEmployeeRelationDto, PartyType, RelationshipTypeCode } from 'types/contacts';
import { MinDto } from 'types/other';

export interface PortfolioSummaryDto {
    totalClientsAndLeads: number;
    totalClientsAndLeadsLastQuarterDelta: number | null;
    activeClients: number;
    totalRevenueYtd: number | null;
    totalRevenueYtdM1: number | null;
    outstandingBalance: number | null;
    overdueAmount: number | null;
}

export enum PortfolioRowType {
    PARTY = 'PARTY',
    GROUP = 'GROUP',
}

export enum PortfolioViewMode {
    GROUP = 'GROUP',
    CONTACTS = 'CONTACTS',
}

// Ownership pool the portfolio is resolved against. Mirrors the backend
// PortfolioScope enum. MY = the caller's own RP/RM parties; USERS = the combined portfolios of
// the selected colleagues; TEAM = everyone sharing a team with the caller; FIRM = every party in
// the tenant. Any non-MY scope requires the PORTFOLIO.VIEW_OTHER privilege (enforced server-side).
export enum PortfolioScope {
    MY = 'MY',
    USERS = 'USERS',
    TEAM = 'TEAM',
    FIRM = 'FIRM',
}

/** A colleague selected in the "People" scope picker. */
export interface PortfolioScopePerson {
    id: number;
    name: string;
}

/** The full scope selection: the active scope plus, for USERS, the chosen colleagues. */
export interface PortfolioScopeState {
    scope: PortfolioScope;
    people: PortfolioScopePerson[];
}

export const DEFAULT_PORTFOLIO_SCOPE: PortfolioScopeState = { scope: PortfolioScope.MY, people: [] };

/**
 * Serialise a scope selection into the `scope` / `userIds` query params shared by the
 * /v1/portfolio/* endpoints. MY (and USERS before any person is chosen) sends nothing so the
 * backend keeps its default own-portfolio behaviour.
 */
export const buildPortfolioScopeParams = (state: PortfolioScopeState): { scope?: string; userIds?: string } => {
    if (state.scope === PortfolioScope.USERS) {
        const ids = state.people.map((person) => person.id);
        return ids.length > 0 ? { scope: PortfolioScope.USERS, userIds: ids.join(',') } : {};
    }
    if (state.scope === PortfolioScope.TEAM || state.scope === PortfolioScope.FIRM) {
        return { scope: state.scope };
    }
    return {};
};

// URL query-param keys that carry the portfolio scope. They reach the list table as part of its
// `preservedParamKeys`, so they survive filter changes and are saved by filter persistence.
export const PORTFOLIO_SCOPE_PARAM_KEYS = ['scope', 'userIds'] as const;

export const PORTFOLIO_VIEW_MODES = [PortfolioViewMode.GROUP, PortfolioViewMode.CONTACTS] as const;

/**
 * Serialise a scope selection for the URL. Unlike {@link buildPortfolioScopeParams} (the API
 * serialisation, which omits an empty USERS selection so the backend keeps MY behaviour), this
 * keeps `scope=USERS` even before anyone is picked so the selector/URL remember the chosen mode.
 */
export const buildPortfolioScopeUrlParams = (state: PortfolioScopeState): { scope?: string; userIds?: string } => {
    if (state.scope === PortfolioScope.MY) {
        return {};
    }
    if (state.scope === PortfolioScope.USERS) {
        const ids = state.people.map((person) => person.id);
        return ids.length > 0
            ? { scope: PortfolioScope.USERS, userIds: ids.join(',') }
            : { scope: PortfolioScope.USERS };
    }
    return { scope: state.scope };
};

/**
 * Parse the `scope` / `userIds` query params back into a scope selection (the inverse of
 * {@link buildPortfolioScopeParams}). People are seeded with id-only placeholder names; the real
 * names are re-resolved by the People picker's label enrichment and a caller-side name cache.
 */
export const parsePortfolioScopeParams = (params: URLSearchParams): PortfolioScopeState => {
    const rawScope = params.get('scope');
    if (rawScope === PortfolioScope.TEAM) {
        return { scope: PortfolioScope.TEAM, people: [] };
    }
    if (rawScope === PortfolioScope.FIRM) {
        return { scope: PortfolioScope.FIRM, people: [] };
    }
    if (rawScope === PortfolioScope.USERS) {
        const ids = (params.get('userIds') ?? '')
            .split(',')
            .map((raw) => Number(raw.trim()))
            .filter((id) => Number.isInteger(id) && id > 0);
        // Empty name = "not resolved yet" — the picker's label enrichment fills it; the UI shows a
        // skeleton in the meantime rather than the raw id.
        return { scope: PortfolioScope.USERS, people: ids.map((id) => ({ id, name: '' })) };
    }
    return DEFAULT_PORTFOLIO_SCOPE;
};

export interface PortfolioListItem {
    rowType: PortfolioRowType;
    /** Group rows: how many contacts are listed under the row, i.e. those left after the
     *  request's filter. Null on party rows. */
    memberCount: number | null;
    /** Group rows: how many contacts the group would list with no filter at all — persona non
     *  grata included, ownership ignored. Null on party rows. */
    totalMemberCount: number | null;
    /** Group rows under a filter panel: true when the group matches the panel on its own columns,
     *  false when it is on the page only because a contact under it matches. Null when the request
     *  carried no panel clause, and on party rows. */
    matchesFilter: boolean | null;
    id: number;
    name: string;
    /** Person party rows only: separate name parts so the list can render last-name-first
     *  under LAST_NAME_FIRST. Null for companies and groups. */
    firstName?: string | null;
    lastName?: string | null;
    /** Legal/registered name for parties when it differs from `name`. Null for groups
     *  and for parties whose official and display names match. */
    officialName: string | null;
    /** Party rows only (null on groups). Surfaced in the list via `<PartyName>`'s
     *  `showPartyNumber`, matching the global contacts list. */
    partyNumber: string | null;
    clientSequence: number | null;
    partyType: PartyType;
    relationshipTypeCode: RelationshipTypeCode;
    segmentTypeCode: string | null;
    serviceLevelTypeCode: string | null;
    clientSince: string | null;
    lastBdEffort: string | null;
    /** Last BD effort of the firm's configured last-effort type; null while the LAST_HCS_MEETING flag is off for
     *  the user or the firm configures none. */
    lastHCSBdEffort: string | null;
    latestTimecardDate: string | null;
    activeMattersCount: number | null;
    isAddingNewCasesBlocked: boolean | null;
    isAddingTimecardsBlocked: boolean | null;
    /** Warning-icon flags mirrored from the global contacts list. Populated on party rows only
     *  (null on group rows). `isTimecardAddingAutoBlocked` is the matters-derived blocked flag;
     *  `complianceVerificationStatusTypeCode` drives the non-verified icon for CLIENT rows. */
    isPersonaNonGrata: boolean | null;
    isTimecardAddingAutoBlocked: boolean | null;
    complianceVerificationStatusTypeCode: VerificationStatusType | null;
    managers: PartyToEmployeeRelationDto[] | null;
    partners: PartyToEmployeeRelationDto[] | null;
    primaryContacts: MinDto[] | null;
    businessAreaCodes: string[];
    sectorGroupNames: string[];
    revenueYtd: number | null;
    revenueYtdM1: number | null;
    /** For group rows: the calling user's slice (RP/RM members only) of the group's
     *  YTD revenue. revenueYtd itself is the FULL group total. Always null for party rows. */
    myOwnershipRevenueYtd: number | null;
    outstandingBalance: number | null;
    overdueAmount: number | null;
    groupPartnershipTypeCode: string | null;
    members?: PortfolioListItem[];
    /** True iff the current user is RP/RM of this row's entity (party rows: party_employee;
     *  group rows: party_group_employee). Drives the orange "my clients" side-border. */
    isMine: boolean | null;
}
